import React, { useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  AlertTriangle,
  ArrowRight,
  Bot,
  CheckCircle2,
  Clipboard,
  Code2,
  Copy,
  ExternalLink,
  FileSearch,
  FileText,
  Globe2,
  Languages,
  Link2,
  ListChecks,
  Map,
  RefreshCw,
  Route,
  Search,
  ShieldAlert,
  ShieldCheck,
  Signal,
  XCircle
} from "lucide-react";
import { sampleAudit } from "./sampleAudit";
import type { AuditCheck, AuditIssue, GeneratedOutput, IndexAudit, Level, PageMetadata, RobotsResult, SitemapInfo } from "./types";
import "./styles.css";

type Language = "en" | "ko";
type OutputKey = keyof GeneratedOutput;

const outputTabs: Array<{ key: OutputKey; label: string }> = [
  { key: "robots", label: "robots.txt" },
  { key: "sitemap", label: "sitemap.xml" },
  { key: "meta", label: "Meta" },
  { key: "markdown", label: "Report" }
];

const text = {
  en: {
    product: "IndexSeal Inspector",
    title: "Public indexability signals in one scan",
    subtitle: "Check robots access, noindex directives, canonical alignment, sitemap discovery, and preview metadata before a page goes live.",
    urlLabel: "Public URL",
    agent: "Crawler",
    scan: "Scan",
    scanning: "Scanning",
    sample: "Sample",
    ready: "Public signals only. Search Console ownership is not required.",
    score: "Index readiness",
    finalUrl: "Final URL",
    crawl: "Crawl map",
    signals: "Signal board",
    metadata: "Page metadata",
    sitemap: "Sitemap",
    fixes: "Generated fixes",
    issues: "Priority queue",
    copy: "Copy",
    copied: "Copied",
    noIssues: "No priority issues in the current result.",
    imageAlt: "IndexSeal visual map of crawler, sitemap, and canonical signals",
    error: "The audit could not run.",
    noteTitle: "Scope boundary",
    noteBody: "This tool reads public files and response metadata. Verified Google index status requires authenticated Search Console access."
  },
  ko: {
    product: "IndexSeal Inspector",
    title: "공개 색인 가능 신호를 한 번에 점검",
    subtitle: "robots 접근, noindex 지시문, canonical 정합성, sitemap 발견, 미리보기 메타데이터를 배포 전에 확인합니다.",
    urlLabel: "공개 URL",
    agent: "크롤러",
    scan: "검사",
    scanning: "검사 중",
    sample: "샘플",
    ready: "공개 신호만 읽습니다. Search Console 소유권 인증은 필요하지 않습니다.",
    score: "색인 준비도",
    finalUrl: "최종 URL",
    crawl: "크롤 맵",
    signals: "신호 보드",
    metadata: "페이지 메타데이터",
    sitemap: "사이트맵",
    fixes: "생성된 수정안",
    issues: "우선순위 큐",
    copy: "복사",
    copied: "복사됨",
    noIssues: "현재 결과에는 우선순위 이슈가 없습니다.",
    imageAlt: "크롤러, 사이트맵, canonical 신호를 보여주는 IndexSeal 시각 맵",
    error: "감사를 실행하지 못했습니다.",
    noteTitle: "범위 기준",
    noteBody: "이 도구는 공개 파일과 응답 메타데이터만 읽습니다. 실제 Google 색인 상태 확인은 Search Console 인증이 필요합니다."
  }
};

function App() {
  const [language, setLanguage] = useState<Language>("en");
  const [targetUrl, setTargetUrl] = useState("https://example.com/");
  const [agent, setAgent] = useState("Googlebot");
  const [audit, setAudit] = useState<IndexAudit>(sampleAudit);
  const [activeOutput, setActiveOutput] = useState<OutputKey>("robots");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const t = text[language];
  const output = audit.generated[activeOutput];
  const priorityIssues = useMemo(() => audit.issues.slice(0, 6), [audit.issues]);

  async function runAudit(event?: React.FormEvent<HTMLFormElement>) {
    event?.preventDefault();
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams({ url: targetUrl, agent });
      const response = await fetch(`/api/audit?${params.toString()}`);
      const payload = (await response.json()) as IndexAudit | { error?: string };
      if (!response.ok) {
        throw new Error("error" in payload ? payload.error ?? t.error : t.error);
      }
      setAudit(payload as IndexAudit);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : t.error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="app-shell">
      <section className="console">
        <header className="topbar">
          <a data-google-vignette="false" className="brand" href="/" aria-label="IndexSeal Inspector home">
            <span className="brand-mark">
              <FileSearch size={22} />
            </span>
            <span>{t.product}</span>
          </a>
          <nav aria-label="Site links">
            <a data-google-vignette="false" href="/about.html">About</a>
            <a data-google-vignette="false" href="/privacy.html">Privacy</a>
          </nav>
          <button className="language-button" type="button" onClick={() => setLanguage(language === "en" ? "ko" : "en")}>
            <Languages size={17} />
            {language.toUpperCase()}
          </button>
        </header>

        <section className="command-panel">
          <div className="command-copy">
            <p className="eyebrow">
              <Signal size={16} />
              robots / sitemap / canonical / noindex
            </p>
            <h1>{t.title}</h1>
            <p>{t.subtitle}</p>
          </div>

          <form className="scan-form" onSubmit={runAudit}>
            <label className="url-field">
              <span>{t.urlLabel}</span>
              <div className="input-frame">
                <Globe2 size={18} />
                <input value={targetUrl} onChange={(event) => setTargetUrl(event.target.value)} spellCheck={false} />
              </div>
            </label>
            <label className="agent-field">
              <span>{t.agent}</span>
              <select value={agent} onChange={(event) => setAgent(event.target.value)}>
                <option value="Googlebot">Googlebot</option>
                <option value="*">*</option>
                <option value="Bingbot">Bingbot</option>
              </select>
            </label>
            <div className="action-row">
              <button className="secondary-button" type="button" onClick={() => setAudit(sampleAudit)}>
                <Route size={17} />
                {t.sample}
              </button>
              <button className="primary-button" type="submit" disabled={loading}>
                {loading ? <RefreshCw className="spin" size={17} /> : <Search size={17} />}
                {loading ? t.scanning : t.scan}
              </button>
            </div>
          </form>

          <p className="scope-line">
            <ShieldCheck size={16} />
            {t.ready}
          </p>
        </section>

        {error ? (
          <div className="error-banner" role="alert">
            <AlertTriangle size={18} />
            {error}
          </div>
        ) : null}

        <section className="score-row" aria-label="Audit summary">
          <ScoreCard audit={audit} label={t.score} />
          <MetricCard icon={<Globe2 size={18} />} label={t.finalUrl} value={audit.fetch.status ? String(audit.fetch.status) : "N/A"} detail={audit.finalUrl} level={audit.fetch.ok ? "pass" : "fail"} />
          <MetricCard icon={<Bot size={18} />} label="Robots" value={audit.robots.allowed ? "Allowed" : "Blocked"} detail={audit.robots.matchedGroup} level={audit.robots.allowed ? "pass" : "fail"} />
          <MetricCard icon={<Map size={18} />} label="Sitemap" value={audit.sitemap.found ? String(audit.sitemap.urlCount) : "Missing"} detail={audit.sitemap.type} level={audit.sitemap.found ? (audit.sitemap.containsTarget ? "pass" : "warn") : "warn"} />
        </section>

        <section className="workspace-grid">
          <div className="primary-column">
            <Panel title={t.crawl} icon={<Route size={18} />}>
              <CrawlMap audit={audit} />
            </Panel>

            <Panel title={t.signals} icon={<ListChecks size={18} />}>
              <SignalBoard checks={audit.checks} />
            </Panel>

            <div className="split-grid">
              <Panel title={t.metadata} icon={<FileText size={18} />}>
                <MetadataPanel metadata={audit.metadata} />
              </Panel>
              <Panel title={t.sitemap} icon={<Map size={18} />}>
                <SitemapPanel sitemap={audit.sitemap} />
              </Panel>
            </div>
          </div>

          <aside className="side-column">
            <figure className="visual-panel">
              <img src="/indexseal-map.png" alt={t.imageAlt} />
            </figure>

            <Panel title={t.fixes} icon={<Code2 size={18} />}>
              <div className="tab-row" role="tablist" aria-label="Generated output">
                {outputTabs.map((tab) => (
                  <button type="button" key={tab.key} className={activeOutput === tab.key ? "active" : ""} onClick={() => setActiveOutput(tab.key)}>
                    {tab.label}
                  </button>
                ))}
              </div>
              <div className="code-box">
                <pre>{output}</pre>
                <CopyButton value={output} copyLabel={t.copy} copiedLabel={t.copied} />
              </div>
            </Panel>

            <Panel title={t.issues} icon={<ShieldAlert size={18} />}>
              <IssueList issues={priorityIssues} emptyText={t.noIssues} />
            </Panel>

            <aside className="scope-card">
              <strong>{t.noteTitle}</strong>
              <p>{t.noteBody}</p>
              <a data-google-vignette="false" href="https://developers.google.com/webmaster-tools/v1/urlInspection.index/inspect" target="_blank" rel="noreferrer">
                URL Inspection API
                <ExternalLink size={14} />
              </a>
            </aside>
          </aside>
        </section>
      </section>
    </main>
  );
}

function ScoreCard({ audit, label }: { audit: IndexAudit; label: string }) {
  const angle = Math.max(0, Math.min(100, audit.score)) * 3.6;
  return (
    <article className="score-card">
      <div className="score-dial" style={{ "--angle": `${angle}deg` } as React.CSSProperties}>
        <span>{audit.score}</span>
      </div>
      <div>
        <p>{label}</p>
        <strong>{audit.scoreLabel}</strong>
        <span>{audit.summary.passes} pass / {audit.summary.warnings} warn / {audit.summary.failures} fail</span>
      </div>
    </article>
  );
}

function MetricCard({ icon, label, value, detail, level }: { icon: React.ReactNode; label: string; value: string; detail: string; level: Level }) {
  return (
    <article className={`metric-card ${level}`}>
      <div className="metric-icon">{icon}</div>
      <div>
        <p>{label}</p>
        <strong>{value}</strong>
        <span>{detail}</span>
      </div>
    </article>
  );
}

function Panel({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="panel">
      <header>
        <div>
          {icon}
          <h2>{title}</h2>
        </div>
      </header>
      {children}
    </section>
  );
}

function CrawlMap({ audit }: { audit: IndexAudit }) {
  const steps = [
    { label: "Request", value: audit.requestedUrl, level: "info" as Level, icon: <Globe2 size={17} /> },
    { label: "Redirects", value: audit.fetch.redirected ? `${audit.fetch.redirectChain.length - 1} hop` : "Direct", level: audit.fetch.redirected ? "warn" as Level : "pass" as Level, icon: <ArrowRight size={17} /> },
    { label: "Robots", value: audit.robots.allowed ? "Allowed" : "Blocked", level: audit.robots.allowed ? "pass" as Level : "fail" as Level, icon: <Bot size={17} /> },
    { label: "Final", value: audit.finalUrl, level: audit.fetch.ok ? "pass" as Level : "fail" as Level, icon: <CheckCircle2 size={17} /> }
  ];

  return (
    <div className="crawl-map">
      {steps.map((step) => (
        <article className={`crawl-step ${step.level}`} key={step.label}>
          <div>{step.icon}</div>
          <p>{step.label}</p>
          <span>{step.value}</span>
        </article>
      ))}
      <RobotsRuleCard robots={audit.robots} />
    </div>
  );
}

function RobotsRuleCard({ robots }: { robots: RobotsResult }) {
  return (
    <article className="robots-rule">
      <div>
        <Bot size={18} />
        <strong>{robots.agent}</strong>
      </div>
      <p>{robots.matchedRule ? robots.matchedRule.sourceLine : robots.found ? "No matching directive" : "No robots file"}</p>
      <span>{robots.url}</span>
    </article>
  );
}

function SignalBoard({ checks }: { checks: AuditCheck[] }) {
  return (
    <div className="signal-board">
      {checks.map((check) => (
        <article className={`signal-row ${check.level}`} key={check.key}>
          <LevelIcon level={check.level} />
          <div>
            <p>{check.group}</p>
            <strong>{check.label}</strong>
            <span>{check.detail}</span>
          </div>
          <em>{check.value}</em>
        </article>
      ))}
    </div>
  );
}

function MetadataPanel({ metadata }: { metadata: PageMetadata }) {
  const rows = [
    ["Title", metadata.title],
    ["Description", metadata.description],
    ["Canonical", metadata.canonical],
    ["Meta robots", metadata.robotsMeta.join(", ") || "None"],
    ["X-Robots-Tag", metadata.xRobotsTag || "None"],
    ["OpenGraph", [metadata.ogTitle, metadata.ogDescription, metadata.ogImage].filter(Boolean).length ? "Present" : "Sparse"]
  ];

  return (
    <div className="fact-list">
      {rows.map(([label, value]) => (
        <div key={label}>
          <span>{label}</span>
          <strong>{value || "Missing"}</strong>
        </div>
      ))}
    </div>
  );
}

function SitemapPanel({ sitemap }: { sitemap: SitemapInfo }) {
  return (
    <div className="sitemap-box">
      <div className={`sitemap-status ${sitemap.containsTarget ? "pass" : sitemap.found ? "warn" : "fail"}`}>
        <Map size={18} />
        <strong>{sitemap.found ? `${sitemap.urlCount} entries` : "No readable sitemap"}</strong>
        <span>{sitemap.url}</span>
      </div>
      <div className="sample-urls">
        {sitemap.sampleUrls.length ? (
          sitemap.sampleUrls.map((url) => (
            <p key={url}>
              <Link2 size={14} />
              {url}
            </p>
          ))
        ) : (
          <p>{sitemap.error || "No sample URLs available."}</p>
        )}
      </div>
    </div>
  );
}

function IssueList({ issues, emptyText }: { issues: AuditIssue[]; emptyText: string }) {
  if (!issues.length) {
    return (
      <div className="empty-state">
        <CheckCircle2 size={18} />
        {emptyText}
      </div>
    );
  }

  return (
    <div className="issue-list">
      {issues.map((issue) => (
        <article className={`issue-card ${issue.level}`} key={`${issue.group}-${issue.title}`}>
          <LevelIcon level={issue.level} />
          <div>
            <strong>{issue.title}</strong>
            <span>{issue.group}</span>
            <p>{issue.detail}</p>
          </div>
        </article>
      ))}
    </div>
  );
}

function LevelIcon({ level }: { level: Level }) {
  if (level === "pass") return <CheckCircle2 className="level pass" size={18} />;
  if (level === "fail") return <XCircle className="level fail" size={18} />;
  if (level === "warn") return <AlertTriangle className="level warn" size={18} />;
  return <Signal className="level info" size={18} />;
}

function CopyButton({ value, copyLabel, copiedLabel }: { value: string; copyLabel: string; copiedLabel: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1300);
  }

  return (
    <button className="copy-button" type="button" onClick={copy}>
      <Copy size={15} />
      {copied ? copiedLabel : copyLabel}
    </button>
  );
}

createRoot(document.getElementById("root")!).render(<App />);
